import React, {useState} from 'react';
import {useMutation} from '@tanstack/react-query';
import Card from '../common/Card';
import {Plus, Check, X, Loader2} from 'lucide-react';
import AdminService from '../../services/adminService';

interface AddTestFormData {
    testName: string;
    color: string;
    layout: string;
    specimenType: string;
    providerNPI: string;
    diagCodes: string;
    inRegisterDrop: boolean;
    inOrderDrop: boolean;
}

export const AddTest: React.FC = () => {
    const [formData, setFormData] = useState<AddTestFormData>({
        testName: '',
        color: '',
        layout: '',
        specimenType: '',
        providerNPI: '',
        diagCodes: '',
        inRegisterDrop: false,
        inOrderDrop: false,
    });

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const addTestMutation = useMutation({
        mutationFn: async (testData: AddTestFormData) => {
            // This would need to be added to AdminService
            const response = await AdminService.addTest(testData);
            return response;
        },
        onSuccess: () => {
            setFormData({
                testName: '',
                color: '',
                layout: '',
                specimenType: '',
                providerNPI: '',
                diagCodes: '',
                inRegisterDrop: false,
                inOrderDrop: false,
            });
            setSuccessMessage('Test added successfully!');
            setErrorMessage('');
        },
        onError: () => {
            setErrorMessage('Failed to add test. Please try again.');
            setSuccessMessage('');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSuccessMessage('');
        setErrorMessage('');

        if (!formData.testName.trim()) {
            setErrorMessage('Test name is required');
            return;
        }

        addTestMutation.mutate(formData);
    };

    const handleInputChange = (field: keyof AddTestFormData, value: string | boolean) => {
        setFormData((prev) => ({...prev, [field]: value}));
    };

    return (
        <div className="space-y-6">
            <Card>
                <Card.Header>
                    <Card.Title className="flex items-center space-x-2">
                        <Plus className="h-5 w-5" />
                        <span>Add New Test</span>
                    </Card.Title>
                </Card.Header>
                <Card.Content>
                    {/* Success/Error Messages */}
                    {(successMessage || errorMessage) && (
                        <div
                            className="mb-6 p-4 rounded-lg flex items-center space-x-2 text-white"
                            style={{backgroundColor: successMessage ? '#42D4B0' : '#F15E87'}}>
                            {successMessage ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
                            <span>{successMessage || errorMessage}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Test Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-mld-text-primary">Basic Information</h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Test Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.testName}
                                        onChange={(e) => handleInputChange('testName', e.target.value)}
                                        placeholder="Enter test name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Color Code
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.color}
                                        onChange={(e) => handleInputChange('color', e.target.value)}
                                        placeholder="e.g., #FF5733"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Layout Type
                                    </label>
                                    <select
                                        value={formData.layout}
                                        onChange={(e) => handleInputChange('layout', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                        <option value="">Select layout</option>
                                        <option value="panel">Panel</option>
                                        <option value="single">Single</option>
                                        <option value="binary">Binary</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Specimen Type
                                    </label>
                                    <select
                                        value={formData.specimenType}
                                        onChange={(e) => handleInputChange('specimenType', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                        <option value="">Select specimen type</option>
                                        <option value="blood">Blood</option>
                                        <option value="urine">Urine</option>
                                        <option value="saliva">Saliva</option>
                                        <option value="swab">Swab</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Provider Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-mld-text-primary">Provider Information</h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Provider NPI
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.providerNPI}
                                        onChange={(e) => handleInputChange('providerNPI', e.target.value)}
                                        placeholder="Enter provider NPI"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                        Diagnosis Codes
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.diagCodes}
                                        onChange={(e) => handleInputChange('diagCodes', e.target.value)}
                                        placeholder="Enter diagnosis codes (comma-separated)"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Availability Options */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-mld-text-primary">Availability</h3>

                            <div className="space-y-3">
                                <label className="flex items-center space-x-3">
                                    <input
                                        type="checkbox"
                                        checked={formData.inRegisterDrop}
                                        onChange={(e) => handleInputChange('inRegisterDrop', e.target.checked)}
                                        className="h-4 w-4 text-mld-primary focus:ring-mld-primary border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-mld-text-primary">
                                        Include in registration dropdown
                                    </span>
                                </label>

                                <label className="flex items-center space-x-3">
                                    <input
                                        type="checkbox"
                                        checked={formData.inOrderDrop}
                                        onChange={(e) => handleInputChange('inOrderDrop', e.target.checked)}
                                        className="h-4 w-4 text-mld-primary focus:ring-mld-primary border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-mld-text-primary">Include in order dropdown</span>
                                </label>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={addTestMutation.isPending}
                            className="flex items-center space-x-2 px-6 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
                            {addTestMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Plus className="h-4 w-4" />
                            )}
                            <span>{addTestMutation.isPending ? 'Adding Test...' : 'Add Test'}</span>
                        </button>
                    </form>
                </Card.Content>
            </Card>
        </div>
    );
};
